import React, { useState } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { Note } from '../../types/types';
import NoteItem from './NoteItem';

interface NotesListProps {
    notes: Note[];
    candidateId: string;
    onAddNote: (candidateId: string, text: string) => Promise<Note>;
    onEditNote: (noteId: string, text: string) => Promise<void>;
    onDeleteNote: (noteId: string) => Promise<void>;
}

const NotesList: React.FC<NotesListProps> = ({
    notes,
    candidateId,
    onAddNote,
    onEditNote,
    onDeleteNote
}) => {
    const [newNoteText, setNewNoteText] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newNoteText.trim()) return;

        setIsSubmitting(true);
        setError(null);

        try {
            await onAddNote(candidateId, newNoteText);
            setNewNoteText('');
        } catch (error) {
            console.error('Error adding note:', error);
            setError('Failed to add note. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <h5 className="mb-4">Candidate Notes</h5>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit} className="mb-4">
                <Form.Group className="mb-3">
                    <Form.Label>Add a new note</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Type your note here..."
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                    />
                </Form.Group>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || !newNoteText.trim()}
                >
                    {isSubmitting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Adding...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-plus-circle me-2"></i>
                            Add Note
                        </>
                    )}
                </Button>
            </Form>

            <div className="notes-list">
                {notes.length === 0 ? (
                    <div className="text-center py-4">
                        <i className="bi bi-journal-text text-muted display-4"></i>
                        <p className="text-muted mt-2">No notes added yet.</p>
                    </div>
                ) : (
                    notes.map((note) => (
                        <NoteItem
                            key={note.id}
                            note={note}
                            onEdit={onEditNote}
                            onDelete={onDeleteNote}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default NotesList;